"use strict";

const _ = require('lodash');
const moment = require('moment');
const TS = require("@zerolight/zlthunder-time-utils");
const DateTimePicker = require('eonasdan-bootstrap-datetimepicker');

const Controller = require('../shared/controller');
const DATEPICKER_ICONS = {
	time: 'fa fa-clock-o',
	date: 'fa fa-calendar',
	up: 'fa fa-chevron-up',
	down: 'fa fa-chevron-down',
	previous: 'fa fa-chevron-left',
	next: 'fa fa-chevron-right',
	today: 'fa fa-bullseye',
	clear: 'fa fa-trash-o',
	close: 'fa fa-times'
};
const RANGE_PRESETS = [['P30D', '30 days'], ['P7D', '7 days'], ['P3D', '3 days']];

function today() {
	return TS.day(new Date());
}

function tomorrow() {
	return TS.addDays(today(), 1);
}

const TIME_RANGE_BASIC_TEMPLATE = require('../templates/time-range-basic.html');
const TIME_RANGE_TEMPLATE = require('../templates/time-range.html');

class TimeRangeBasic extends Controller {
	constructor(... args) {
		super({
			id: _.uniqueId('time-range-'),
			template: TIME_RANGE_BASIC_TEMPLATE,
			minRange: 3 * TS.DAY,
			defaultRange: 30 * TS.DAY,
			format: 'YYYY-MM-DD HH:mm'
		}, ...arguments);
		this.setTimeRange(this.options);
		_.bindAll(this, 'onTimeFromChange', 'onTimeToChange');
	}

	get minRange() {
		return this.options.minRange;
	}

	get defaultRange() {
		return this.options.defaultRange;
	}

	get timeTo() {
		return moment(this._timeTo);
	}

	get timeFrom() {
		return moment(this._timeFrom);
	}

	setTimeRange(options) {
		let {timeFrom, timeTo} = options || {};
		let minRange = this.minRange;
		if (timeFrom && timeTo) {
			timeFrom = TS.validate(timeFrom);
			timeTo = TS.validate(timeTo);
			if (timeFrom > timeTo) {
				timeTo = TS.alter(timeFrom, minRange);
			}
		} else if (timeFrom) {
			timeFrom = TS.validate(timeFrom);
			timeTo = this._timeTo || TS.alter(timeFrom, minRange);
		} else if (timeTo) {
			timeTo = TS.validate(timeTo);
			timeFrom = this._timeFrom || TS.alter(timeTo, -minRange);
		} else {
			let defaults = this.defaultTimeRange();
			timeTo = this._timeTo || defaults.timeTo;
			timeFrom = this._timeFrom || defaults.timeFrom;
		}
		this._timeTo = timeTo;
		this._timeFrom = timeFrom;
	}

	defaultTimeRange() {
		let t = tomorrow();
		return {timeFrom: TS.alter(t, -this.defaultRange), timeTo: t}
	}

	get $timeFromInput() {
		return this.$.find('.cm-time-from-input');
	}

	get $timeToInput() {
		return this.$.find('.cm-time-to-input');
	}

	get timeFromDTP() {
		return this.$timeFromInput.data('DateTimePicker');
	}

	get timeToDTP() {
		return this.$timeToInput.data('DateTimePicker');
	}

	onTimeToChange(e) {
		this.setTimeRange({timeTo: e.date});
		this.emit('cm:update');
		this.update();
	}

	onTimeFromChange(e) {
		this.setTimeRange({timeFrom: e.date});
		this.emit('cm:update');
		this.update();
	}

	setDOMListeners() {
		this.$timeFromInput.on('dp.change', this.onTimeFromChange);
		this.$timeToInput.on('dp.change', this.onTimeToChange);
	}

	unsetDOMListeners() {
		this.$timeFromInput.off('dp.change', this.onTimeFromChange);
		this.$timeToInput.off('dp.change', this.onTimeToChange);
	}

	updateDOM() {
		this.unsetDOMListeners();
		this.timeFromDTP.date(this.timeFrom);
		this.timeToDTP.date(this.timeTo);
		this.setDOMListeners();
	}

	getRenderOptions(options, callback) {
		this.setTimeRange(options);
		return super.getRenderOptions(options, callback);
	}

	draw(options) {
		options = super.draw(options);
		let format = options.format;
		this.$timeFromInput.datetimepicker({
			icons: DATEPICKER_ICONS, format: format, defaultDate: this.timeFrom, useCurrent: false});
		this.$timeToInput.datetimepicker({
			icons: DATEPICKER_ICONS, format: format, defaultDate: this.timeTo});
		this.updateDOM();
	}

	redraw(options) {
		this.updateDOM();
		return options;
	}

	getQueryParams() {
		// We want timeFrom to be excluded, API does inclusive match.
		return {to: TS.alter(this._timeTo, -1000), from: this._timeFrom};
	}

	getPanelParams() {
		return this.getQueryParams();
	}

	getInfoContent() {
		return moment.duration(this.timeTo - this.timeFrom).humanize();
	}

	reset() {
		let defaults = this.defaultTimeRange();
		this._timeTo = defaults.timeTo;
		this._timeFrom = defaults.timeFrom;
		this.updateDOM();
	}
}

class TimeRangeWithPresets extends TimeRangeBasic {
	constructor(... args) {
		super({
			template: TIME_RANGE_TEMPLATE,
			rangePresets: RANGE_PRESETS
		}, ...arguments);
		_.bindAll(this, 'onTimeRangeChange');
	}

	get timeRange() {
		return this._timeRange;
	}

	get $timeRangeRadio() {
		return this.$.find('.cm-time-range-radio input[type="radio"]');
	}

	onTimeRangeChange(e) {
		let timeRange = moment.duration(e.target.value).valueOf();
		this.setTimeRange({timeRange: timeRange});
		this.emit('cm:update');
		this.update();
	}

	setDOMListeners() {
		super.setDOMListeners();
		this.$timeRangeRadio.on('change', this.onTimeRangeChange);
	}

	unsetDOMListeners() {
		super.unsetDOMListeners();
		this.$timeRangeRadio.off('change', this.onTimeRangeChange);
	}
}

class TimeRangeBackward extends TimeRangeWithPresets {
	setTimeRange(options) {
		let {timeFrom, timeTo, timeRange} = options || {};
		timeRange = !_.isUndefined(timeRange) ? timeRange : this.timeRange;
		if (!timeRange) {
			super.setTimeRange({timeFrom: timeFrom, timeTo: timeTo});
			this._timeRange = 0;
			return;
		}

		timeTo = timeTo ? TS.validate(timeTo) : this._timeTo;
		this._timeTo = timeTo;
		this._timeFrom = TS.alter(timeTo, -timeRange);
		this._timeRange = timeRange;
	}

	onTimeRangeChange(e) {
		super.onTimeRangeChange(e);
		this.$timeFromInput.prop('disabled', Boolean(this.timeRange));
	}
}

class TimeRangeForward extends TimeRangeWithPresets {
	constructor(... args) {
		super({
			template: TIME_RANGE_TEMPLATE,
			rangePresets: RANGE_PRESETS
		}, ...arguments);
		_.bindAll(this, 'onTimeRangeChange');
	}

	setTimeRange(options) {
		let {timeFrom, timeTo, timeRange} = options || {};
		timeRange = !_.isUndefined(timeRange) ? timeRange : this._timeRange;
		if (!timeRange) {
			super.setTimeRange({timeFrom: timeFrom, timeTo: timeTo});
			this._timeRange = 0;
			return;
		}

		timeFrom = timeFrom ? TS.validate(timeFrom) : this._timeFrom;
		this._timeFrom = timeFrom;
		this._timeTo = TS.alter(timeFrom, timeRange);
		this._timeRange = timeRange;
	}

	onTimeRangeChange(e) {
		super.onTimeRangeChange(e);
		this.$timeToInput.prop('disabled', Boolean(this.timeRange));
	}
}

module.exports = exports = TimeRangeBackward;
exports.TimeRangeBackward = TimeRangeBackward;
exports.TimeRangeForward = TimeRangeForward;
exports.TimeRangeBasic = TimeRangeBasic;
exports.TimeRangeWithPresets = TimeRangeWithPresets;